import React from 'react';
import { InputNumber } from 'antd';
import { SwapRightOutlined } from '@ant-design/icons';
import { useFilterLabelClassName, useFilterContainerClassName, useFilterInputContainerClassName } from '../../../utils/languageUtils';
import { t } from '../../../i18n';

interface NumberRangeFilterItemProps {
    label: string;
    value?: {
        min?: number;
        max?: number;
    };
    onChange?: (value: { min?: number; max?: number } | undefined) => void;
    className?: string;
    spanClassName?: string;
    placeholder?: [string, string];
    min?: number;
    max?: number;
    disabled?: boolean;
    inputWidth?: string; // 输入框宽度，例如 'w-20', 'w-24' 等，默认为 'flex-1'
}

const NumberRangeFilterItem: React.FC<NumberRangeFilterItemProps> = ({
    label,
    value,
    onChange,
    className = '',
    spanClassName = '',
    placeholder,
    min,
    max,
    disabled = false,
    inputWidth = 'flex-1'
}) => {
    // 使用统一的样式 Hook，根据语言自动返回合适的样式类名
    const containerClassName = useFilterContainerClassName(className);
    const labelClassName = useFilterLabelClassName(spanClassName);
    const inputContainerClassName = useFilterInputContainerClassName();

    // 默认占位符（使用国际化）
    const defaultPlaceholder: [string, string] = [
        t('components.input.min') || 'Min',
        t('components.input.max') || 'Max'
    ];

    const handleMinChange = (val: number | null) => {
        if (onChange) {
            const newMin = val ?? undefined;
            const currentMax = value?.max;
            
            // 验证：最小值不能大于最大值
            if (newMin !== undefined && currentMax !== undefined && newMin > currentMax) {
                // 如果最小值大于最大值，自动调整最大值为最小值
                const newValue = {
                    min: newMin,
                    max: newMin
                };
                onChange(newValue);
            } else {
                const newValue = {
                    min: newMin,
                    max: currentMax
                };
                // 如果两个值都为空，则返回 undefined
                // 修复：使用 == null 同时检查 null 和 undefined，并且确保 0 被视为有效值
                if (newValue.min == null && newValue.max == null) {
                    onChange(undefined);
                } else {
                    // 只要有一个值有效就传递（允许单边界值）
                    onChange(newValue);
                }
            }
        }
    };

    const handleMaxChange = (val: number | null) => {
        if (onChange) {
            const newMax = val ?? undefined;
            const currentMin = value?.min;
            
            // 验证：最大值不能小于最小值
            if (newMax !== undefined && currentMin !== undefined && newMax < currentMin) {
                // 如果最大值小于最小值，自动调整最小值为最大值
                const newValue = {
                    min: newMax,
                    max: newMax
                };
                onChange(newValue);
            } else {
                const newValue = {
                    min: currentMin,
                    max: newMax
                };
                // 如果两个值都为空，则返回 undefined
                // 修复：使用 == null 同时检查 null 和 undefined，并且确保 0 被视为有效值
                if (newValue.min == null && newValue.max == null) {
                    onChange(undefined);
                } else {
                    // 只要有一个值有效就传递（允许单边界值）
                    onChange(newValue);
                }
            }
        }
    };

    return (
        <div className={containerClassName}>
            <span className={labelClassName}>
                {label}
            </span>
            <div className={inputContainerClassName}>
                <div className="flex items-center gap-1 w-full">
                    <InputNumber
                        value={value?.min}
                        onChange={handleMinChange}
                        placeholder={placeholder?.[0] || defaultPlaceholder[0]}
                        min={min}
                        max={value?.max !== undefined ? value.max : max}
                        disabled={disabled}
                        size="middle"
                        className={inputWidth}
                        controls={true}
                    />
                    <SwapRightOutlined className="text-gray-400 flex-shrink-0" style={{ fontSize: '14px' }} />
                    <InputNumber
                        value={value?.max}
                        onChange={handleMaxChange}
                        placeholder={placeholder?.[1] || defaultPlaceholder[1]}
                        min={value?.min !== undefined ? value.min : min}
                        max={max}
                        disabled={disabled}
                        size="middle"
                        className={inputWidth}
                        controls={true}
                    />
                </div>
            </div>
        </div>
    );
};

export default NumberRangeFilterItem;
